<template>
  <q-page class="flex flex-center" style="min-height: 100vh">
    <q-card class="register-card q-pa-lg" style="width: 100%; max-width: 420px">
      <q-card-section class="text-center q-mb-md">
        <img :src="asupcaLogo" alt="ASUPCA" style="height: 72px; width: auto; display: inline-block; margin-bottom: 4px;" />
        <p style="margin: 0; font-size: 15px; font-weight: 500; line-height: 1.35; color: var(--oa-text-secondary);">Asociación de Stand Up Paddle<br />de la Costa Atlántica</p>
      </q-card-section>

      <q-card-section>
        <p class="text-oa-secondary text-center q-mb-md" style="margin-top: 0; padding-bottom: 10px;">Creá tu cuenta</p>

        <q-banner
          v-if="error"
          class="bg-negative text-white q-mb-md"
          rounded
          dense
        >
          {{ error }}
        </q-banner>

        <q-form @submit.prevent="onSubmit" class="q-gutter-md">
          <q-input
            v-model="firstName"
            label="Nombre"
            outlined
            :rules="[(val: string) => !!val || 'El nombre es obligatorio']"
            lazy-rules
          >
            <template #prepend>
              <q-icon name="person" />
            </template>
          </q-input>

          <q-input
            v-model="lastName"
            label="Apellido"
            outlined
            :rules="[(val: string) => !!val || 'El apellido es obligatorio']"
            lazy-rules
          >
            <template #prepend>
              <q-icon name="person" />
            </template>
          </q-input>

          <q-input
            v-model="email"
            type="email"
            label="Email"
            outlined
            :rules="[
              (val: string) => !!val || 'El email es obligatorio',
              (val: string) => /.+@.+\..+/.test(val) || 'Email inválido',
            ]"
            lazy-rules
          >
            <template #prepend>
              <q-icon name="email" />
            </template>
          </q-input>

          <q-input
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            label="Contraseña"
            outlined
            :rules="[
              (val: string) => !!val || 'La contraseña es obligatoria',
              (val: string) => val.length >= 8 || 'Mínimo 8 caracteres',
            ]"
            lazy-rules
          >
            <template #prepend>
              <q-icon name="lock" />
            </template>
            <template #append>
              <q-icon
                :name="showPassword ? 'visibility_off' : 'visibility'"
                class="cursor-pointer"
                @click="showPassword = !showPassword"
              />
            </template>
          </q-input>

          <q-input
            v-model="confirmPassword"
            :type="showPassword ? 'text' : 'password'"
            label="Confirmar contraseña"
            outlined
            :rules="[
              (val: string) => !!val || 'Confirmá tu contraseña',
              (val: string) => val === password || 'Las contraseñas no coinciden',
            ]"
            lazy-rules
          >
            <template #prepend>
              <q-icon name="lock" />
            </template>
          </q-input>

          <div>
            <q-btn
              type="submit"
              label="Crear cuenta"
              color="primary"
              text-color="white"
              class="full-width"
              size="lg"
              :loading="loading"
              :disable="loading"
              no-caps
            />
          </div>
        </q-form>
      </q-card-section>

      <q-card-section class="text-center">
        <p class="text-oa-secondary">
          Ya tenés cuenta?
          <router-link to="/login" style="text-decoration: none; color: var(--oa-gold-full)">
            Iniciá sesión
          </router-link>
        </p>
      </q-card-section>
    </q-card>
  </q-page>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from 'stores/useAuthStore';
import { AxiosError } from 'axios';
import asupcaLogo from 'src/assets/asupca-logo.png';

const router = useRouter();
const authStore = useAuthStore();

const firstName = ref('');
const lastName = ref('');
const email = ref('');
const password = ref('');
const confirmPassword = ref('');
const showPassword = ref(false);
const loading = ref(false);
const error = ref('');

async function onSubmit() {
  if (password.value !== confirmPassword.value) {
    error.value = 'Las contraseñas no coinciden';
    return;
  }

  error.value = '';
  loading.value = true;

  try {
    await authStore.register({
      email: email.value,
      password: password.value,
      firstName: firstName.value,
      lastName: lastName.value,
    });
    await router.push('/');
  } catch (err) {
    if (err instanceof AxiosError && err.response?.data?.message) {
      error.value = err.response.data.message as string;
    } else {
      error.value = 'Error al crear la cuenta. Intentá de nuevo.';
    }
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped lang="scss">
.register-card {
  background: var(--color-white-cap);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-prominent);
  border: none;
  border-top: 3px solid var(--color-ocean-light);
  transition: box-shadow var(--transition-base);
}
</style>
